/**
 * Fidelity V0: canned scenes for headless frame capture (`?fixture=<name>`).
 * Pure — no DOM. States come from sim/ constructors so the snapshot codec
 * always accepts them; positions/states are then set by hand.
 */
import { addPlayer, createState, placeForRound } from '../../sim/state.js'
import { DISC_FLIGHT_Y, DISC_THROW_SPEED, SHIELD_PERFECT_TICKS } from '../../sim/constants.js'
import type { MatchPhase, PlayerState, PlayerStats, SimState, Team } from '../../sim/types.js'
import type { SimEvent } from '../../shared/events.js'
import type { MatchResultMsg } from '../../shared/protocol.js'

export interface FixtureEvent { atMs: number; ev: SimEvent }
export interface Fixture {
  state: SimState
  events: FixtureEvent[]
  mode: 'hub' | 'match'
  cameraTeam: Team
  localId: string | null
  spectator: boolean
  /** Default capture time (ms after the fixture epoch). */
  t: number
  /** Present only for the results fixture. */
  result?: MatchResultMsg
}

const A1 = 'fx-a1', A2 = 'fx-a2', B1 = 'fx-b1', B2 = 'fx-b2'

function match(phase: MatchPhase, o: { players?: 2 | 4; roundIndex?: number; timerTicks?: number; arenaId?: string } = {}): SimState {
  const s = createState({ mode: 'match', kind: 'pvp', seed: 7, arenaId: o.arenaId })
  addPlayer(s, A1, 0, 'ALPHA', ['banker'])
  addPlayer(s, B1, 1, 'BRAVO', ['heavy_disc'])
  if ((o.players ?? 2) === 4) { addPlayer(s, A2, 0, 'ALTO', ['riposte']); addPlayer(s, B2, 1, 'BASS', ['sprinter']) }
  placeForRound(s) // spawn slots + one disc each in hand (03 §1)
  s.match.phase = phase; s.match.phaseTicks = phase === 'LIVE' ? 300 : 0 // LIVE scenes sit 5 s in, past the 80 ms GO pour (11 §8)
  s.match.roundIndex = o.roundIndex ?? 0; s.match.roundNumber = (o.roundIndex ?? 0) + 1
  s.match.timerTicks = o.timerTicks ?? 60 * 60
  s.tick = 600
  return s
}
const P = (s: SimState, id: string): PlayerState => s.players.find((p) => p.id === id)!
function moveTo(p: PlayerState, x: number, z: number, vx = 0, vz = 0): void { p.pos.x = x; p.pos.z = z; p.vel.x = vx; p.vel.z = vz }
function aimAt(p: PlayerState, x: number, z: number): void { const dx = x - p.pos.x, dz = z - p.pos.z, l = Math.hypot(dx, dz) || 1; p.aim.x = dx / l; p.aim.z = dz / l }
/** Take the held disc out of `p`'s hand and put it in flight from `p` toward (tx,tz). */
function throwDisc(s: SimState, p: PlayerState, tx: number, tz: number, distance: number): void {
  const id = p.discsHeld[0]; const d = s.discs.find((q) => q.id === id)!
  p.discsHeld = p.discsHeld.filter((q) => q !== id)
  const dx = tx - p.pos.x, dz = tz - p.pos.z, l = Math.hypot(dx, dz) || 1
  d.state = 'FLIGHT'; d.holder = null; d.thrower = p.id; d.stateTicks = 12
  d.pos.x = p.pos.x + (dx / l) * distance; d.pos.z = p.pos.z + (dz / l) * distance; d.pos.y = DISC_FLIGHT_Y
  d.vel.x = (dx / l) * DISC_THROW_SPEED; d.vel.z = (dz / l) * DISC_THROW_SPEED
}
const view = (state: SimState, over: Partial<Fixture> = {}): Fixture => ({ state, events: [], mode: state.mode, cameraTeam: 0, localId: A1, spectator: false, t: 0, ...over })

export const FIXTURES: Record<string, () => Fixture> = {
  'hub-idle': () => {
    const s = createState({ mode: 'hub', seed: 3 })
    addPlayer(s, A1, 0, 'ALPHA', ['banker']); addPlayer(s, B1, 1, 'BRAVO', ['heavy_disc']); addPlayer(s, A2, 0, 'ALTO', ['riposte'])
    for (const p of s.players) p.invulnUntilTick = 0
    moveTo(P(s, B1), 2, 20, -3, 0); moveTo(P(s, A2), -3, 8, 0, 2)
    return view(s, { t: 400 })
  },
  'live-aim': () => { const s = match('LIVE'); moveTo(P(s, B1), 1.5, 17, -4, 0); aimAt(P(s, A1), 1.5, 17); return view(s) },
  'live-windup': () => { const s = match('LIVE'); const me = P(s, A1); me.state = 'THROW_WINDUP'; me.stateTicks = 1; aimAt(me, 0, 20); return view(s, { t: 25 }) },
  'live-flight': () => {
    const s = match('LIVE'); const me = P(s, A1); me.state = 'THROW_RECOVERY'; me.stateTicks = 4; aimAt(me, 0, 24)
    const discId = me.discsHeld[0]
    throwDisc(s, me, 0, 24, 8)
    return view(s, { events: [{ atMs: 0, ev: { type: 'throw', tick: 590, playerId: A1, discId, x: me.pos.x, z: me.pos.z } }], t: 300 })
  },
  'live-returning': () => {
    const s = match('LIVE'); const me = P(s, A1); throwDisc(s, me, 0, 24, 14)
    const d = s.discs.find((q) => q.state === 'FLIGHT')!; d.state = 'RETURNING'; d.vel.x = -d.vel.x * 0.5; d.vel.z = -d.vel.z * 0.5
    return view(s, { t: 300 })
  },
  'shield-perfect': () => {
    const s = match('LIVE', { players: 4 }); const tm = P(s, A2); tm.state = 'SHIELD'; tm.shieldAgeTicks = 3; moveTo(tm, -2, 6); aimAt(tm, 0, 24)
    return view(s, { events: [{ atMs: 0, ev: { type: 'shieldRaise', tick: 598, playerId: A2 } }], t: 50 })
  },
  'shield-hold': () => {
    const s = match('LIVE', { players: 4 }); const tm = P(s, A2); tm.state = 'SHIELD'; tm.shieldAgeTicks = SHIELD_PERFECT_TICKS + 40; moveTo(tm, -2, 6); aimAt(tm, 0, 24)
    return view(s, { events: [{ atMs: 0, ev: { type: 'shieldRaise', tick: 540, playerId: A2 } }], t: 900 })
  },
  'dash': () => {
    const s = match('LIVE'); const me = P(s, A1); me.state = 'DASHING'; me.stateTicks = 5; moveTo(me, -1, 5, 14, 4)
    return view(s, { events: [{ atMs: 0, ev: { type: 'dash', tick: 595, playerId: A1, dx: 0.96, dz: 0.28 } }], t: 80 })
  },
  // S10 A11-J1: local athlete at the jump apex (1.44 m) with an enemy disc passing underneath; the shadow
  // stays on the floor and shrinks. jump-land: touchdown, landing lock half run (JUMP_LAND_TICKS 6 → 3).
  'jump-apex': () => {
    const s = match('LIVE'); const me = P(s, A1); moveTo(me, 0, 6); aimAt(me, 0, 24)
    me.grounded = false; me.pos.y = 1.44; me.vel.y = 0
    const foe = P(s, B1); aimAt(foe, 0, 6); const discId = foe.discsHeld[0]
    throwDisc(s, foe, 0, 6, 20.4) // disc at z ≈ 6.6, under the athlete
    return view(s, { events: [{ atMs: 0, ev: { type: 'throw', tick: 574, playerId: B1, discId, x: foe.pos.x, z: foe.pos.z } }], t: 850 })
  },
  'jump-land': () => {
    const s = match('LIVE'); const me = P(s, A1); moveTo(me, 0, 6); aimAt(me, 0, 24)
    me.grounded = true; me.pos.y = 0; me.landTicks = 3
    return view(s, { events: [{ atMs: 0, ev: { type: 'land', tick: 597, playerId: A1 } }], t: 50 })
  },
  'exhaust': () => {
    const s = match('LIVE'); const me = P(s, A1); me.state = 'EXHAUST'; me.stateTicks = 20; me.stamina = 0
    return view(s, { events: [{ atMs: 0, ev: { type: 'exhaust', tick: 580, playerId: A1 } }], t: 350 })
  },
  'countdown-2': () => { const s = match('COUNTDOWN'); s.match.phaseTicks = 60; return view(s, { events: [{ atMs: 0, ev: { type: 'countdown', tick: 600, n: 2 } }], t: 300 }) },
  'last10': () => { const s = match('LIVE', { timerTicks: 8 * 60 }); return view(s, { t: 250 }) },
  'overtime': () => {
    const s = match('OVERTIME'); s.match.overtimeTicks = 20 * 60; s.match.phaseTicks = 24 // past the one stroke-only frame (11 §8)
    for (const tm of [0, 1] as Team[]) { const b = s.match.bounds[tm]; b.xMin += 3; b.xMax -= 3 }
    return view(s, { events: [{ atMs: 0, ev: { type: 'overtime', tick: 600 } }], t: 400 })
  },
  'round-end': () => {
    const s = match('ROUND_END', { players: 4 }); s.match.phaseTicks = 30; const loser = P(s, B2); loser.alive = false
    return view(s, {
      events: [
        { atMs: 0, ev: { type: 'derez', tick: 570, playerId: B2, attackerId: A1, discId: 1 } },
        { atMs: 200, ev: { type: 'roundEnd', tick: 600, roundNumber: 1, winner: 0, reason: 'elimination', void: false, score: [1, 0] } },
      ],
      t: 1400,
    })
  },
  'undraw-300': () => {
    const s = match('LIVE', { players: 4 }); const tm = P(s, A2); tm.alive = false; moveTo(tm, -2, 7)
    return view(s, { events: [{ atMs: 0, ev: { type: 'derez', tick: 590, playerId: A2, attackerId: B1, discId: 3 } }], t: 300 })
  },
  'results': () => {
    const s = match('RESULTS', { players: 4 }); s.match.score = [2, 1]; s.match.winner = 0
    const stats = (o: Partial<PlayerStats>): PlayerStats => ({ eliminations: 0, bankEliminations: 0, perfectDeflects: 0, lateBlocks: 0, throws: 0, catches: 0, steals: 0, dashes: 0, jumps: 0, shieldTicks: 0, survived: 0, deaths: 0, ...o })
    const result: MatchResultMsg = {
      roomId: 'm-fixture', winner: 0, score: [2, 1], coinUsed: false, voidRounds: 0, kind: 'pvp', rematch: [], decidingPlayerId: A1,
      accolades: { [A1]: 'Closer', [A2]: 'Wall', [B1]: 'Survivor', [B2]: null },
      players: [
        { id: A1, name: 'ALPHA', team: 0, stats: stats({ eliminations: 3, bankEliminations: 1, catches: 4, dashes: 6, shieldTicks: 252 }), powerUps: ['banker'], accolade: null, bot: false },
        { id: A2, name: 'ALTO', team: 0, stats: stats({ eliminations: 1, perfectDeflects: 3, catches: 3, dashes: 3, shieldTicks: 60 }), powerUps: ['riposte'], accolade: null, bot: false },
        { id: B1, name: 'BRAVO', team: 1, stats: stats({ eliminations: 2, catches: 5, dashes: 5, shieldTicks: 402, survived: 2 }), powerUps: ['heavy_disc'], accolade: null, bot: false },
        { id: B2, name: 'BASS', team: 1, stats: stats({ eliminations: 0, dashes: 2 }), powerUps: ['sprinter'], accolade: null, bot: true },
      ],
      rounds: [
        { roundNumber: 1, winner: 0, reason: 'elimination', void: false, durationTicks: 1800, overtime: false },
        { roundNumber: 2, winner: 1, reason: 'elimination', void: false, durationTicks: 2400, overtime: false },
        { roundNumber: 3, winner: 0, reason: 'ladder_round_elims', void: false, durationTicks: 4200, overtime: true },
      ],
    }
    return view(s, { result, t: 6000 })
  },
  'spectator': () => { const s = match('LIVE', { players: 4 }); return view(s, { localId: null, spectator: true, cameraTeam: 1, t: 200 }) },
  // S10 A02-15: corridor_xl (40 × 16) through its own camera — the far wall sits where corridor_a's does, athletes read 3/4 size,
  // annotations 13.33 / 16.00 / GAP 13.33. Same compositions as live-aim / hub-idle at 4/3 scale.
  'xl-live-aim': () => { const s = match('LIVE', { arenaId: 'corridor_xl' }); moveTo(P(s, B1), 2, 22.67, -4, 0); aimAt(P(s, A1), 2, 22.67); return view(s) },
  'xl-hub-idle': () => {
    const s = createState({ mode: 'hub', seed: 3, arenaId: 'corridor_xl' })
    addPlayer(s, A1, 0, 'ALPHA', ['banker']); addPlayer(s, B1, 1, 'BRAVO', ['heavy_disc']); addPlayer(s, A2, 0, 'ALTO', ['riposte'])
    for (const p of s.players) p.invulnUntilTick = 0
    moveTo(P(s, B1), 2.67, 26.67, -3, 0); moveTo(P(s, A2), -4, 10.67, 0, 2)
    return view(s, { t: 400 })
  },
}
export const FIXTURE_NAMES: readonly string[] = Object.keys(FIXTURES)
